import { createAction } from "@reduxjs/toolkit";
import type { ICastMemberRef } from "dirplayer-js-api";
import { createCompatReducer } from "./createCompatReducer";
import { isUIShown } from "../utils/debug";

export type TSelectedObjectSprite = {
  type: 'sprite',
  spriteNumber: number
}

export type TScoreSpanRef = {
  channelNumber: number,
  frameNumber: number,
  scoreRef: 'stage' | ICastMemberRef
}

export type TSelectedObjectScoreSpan = {
  type: 'scoreSpan',
  spanRef: TScoreSpanRef
}

export type TSelectedObjectMember = {
  type: 'member',
  memberRef: ICastMemberRef
}

export type TSelectedObjectScoreBehavior = {
  type: 'scoreBehavior',
  frameNumber: number
}

export type TSelectedObject = TSelectedObjectSprite | TSelectedObjectMember | TSelectedObjectScoreBehavior | TSelectedObjectScoreSpan

export type TScriptViewMode = 'lingo' | 'assembly';

interface UISliceState {
  selectedObject?: TSelectedObject
  scriptViewMode: TScriptViewMode
  // Whether a loaded movie gets the studio's inspector panels or a bare stage.
  // Seeded from how the build was made (REACT_APP_SHOW_UI, a `#debug` hash, or
  // Electron — where the studio IS the product), then handed to the load
  // screen's checkbox for the rest of the session. It lives here rather than in
  // LoadMovie because DirStudio is the component that acts on it, and the two
  // are not in the same subtree once a movie is loaded.
  showDebugUi: boolean
}

const initialState: UISliceState = {
  scriptViewMode: 'lingo',
  showDebugUi: isUIShown()
}

export const onMemberSelected = createAction<ICastMemberRef>('ui/onMemberSelected')
export const channelSelected = createAction<number>('ui/channelSelected')
export const scoreSpanSelected = createAction<TScoreSpanRef>('ui/scoreSpanSelected')
export const scoreBehaviorSelected = createAction<{ frameNumber: number }>('ui/scoreBehaviorSelected')
export const scriptViewModeChanged = createAction<TScriptViewMode>('ui/scriptViewModeChanged')
export const debugUiToggled = createAction<boolean>('ui/debugUiToggled')

const uiReducer = createCompatReducer(initialState, (builder) => {
  builder
    .addCase(onMemberSelected, (state, action) => {
      return {
        ...state,
        selectedObject: {
          type: 'member',
          memberRef: action.payload
        }
      }
    })
    .addCase(channelSelected, (state, action) => {
      return {
        ...state,
        selectedObject: {
          type: 'sprite',
          spriteNumber: action.payload
        }
      }
    })
    .addCase(scoreSpanSelected, (state, action) => {
      return {
        ...state,
        selectedObject: {
          type: 'scoreSpan',
          spanRef: action.payload
        }
      }
    })
    .addCase(scoreBehaviorSelected, (state, action) => {
      return {
        ...state,
        selectedObject: {
          type: 'scoreBehavior',
          frameNumber: action.payload.frameNumber
        }
      }
    })
    .addCase(scriptViewModeChanged, (state, action) => {
      return {
        ...state,
        scriptViewMode: action.payload
      }
    })
    .addCase(debugUiToggled, (state, action) => {
      return {
        ...state,
        showDebugUi: action.payload
      }
    })
})

export const selectSelectedMemberRef = (state: UISliceState) => state.selectedObject?.type === 'member' ? state.selectedObject.memberRef : undefined
export const selectScriptViewMode = (state: UISliceState) => state.scriptViewMode
export const selectShowDebugUi = (state: UISliceState) => state.showDebugUi

export default uiReducer
